import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlertTriangle, Loader2, Trash2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

export default function DangerZone() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  if (user?.role === "admin") return null;

  const submit = async () => {
    if (confirm.trim().toUpperCase() !== "ELIMINAR") { toast.error("Escribe ELIMINAR para confirmar."); return; }
    setBusy(true);
    try {
      await api.post("/account/delete", { confirm });
      toast.success("Tu cuenta y tus datos fueron eliminados.");
      setUser(null);
      navigate("/");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
      setBusy(false);
    }
  };

  return (
    <div className="mt-8 rounded-2xl bg-white border border-red-200 p-6" data-testid="danger-zone">
      <div className="flex items-start gap-3">
        <span className="h-10 w-10 rounded-xl bg-red-50 grid place-items-center shrink-0">
          <AlertTriangle className="h-5 w-5 text-red-600" />
        </span>
        <div className="flex-1">
          <h3 className="font-heading font-bold text-xuma-dark">Eliminar mi cuenta</h3>
          <p className="text-sm text-xuma-muted mt-1">
            Se eliminarán tu cuenta, tu tarjeta digital y tus datos (prospectos, citas y conversaciones).
            Esta acción es permanente. Te recomendamos exportar tu información antes de continuar.
          </p>
          <Button data-testid="open-delete-account" variant="outline" onClick={() => { setConfirm(""); setOpen(true); }}
            className="mt-4 rounded-full h-10 gap-2 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700">
            <Trash2 className="h-4 w-4" /> Eliminar mi cuenta
          </Button>
        </div>
      </div>

      <Dialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
        <DialogContent data-testid="delete-account-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-red-600">Eliminar cuenta definitivamente</DialogTitle>
            <DialogDescription>
              Esta acción no se puede deshacer. Perderás el acceso a tu tarjeta, tu CRM y tu configuración.
              Para confirmar, escribe <b className="text-xuma-dark">ELIMINAR</b>.
            </DialogDescription>
          </DialogHeader>
          <Input data-testid="delete-confirm-input" value={confirm} onChange={(e) => setConfirm(e.target.value)}
            placeholder="ELIMINAR" autoFocus />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy}>Cancelar</Button>
            <Button data-testid="confirm-delete-account" onClick={submit} disabled={busy || confirm.trim().toUpperCase() !== "ELIMINAR"}
              className="bg-red-600 hover:bg-red-700 text-white gap-2">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />} Sí, eliminar todo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
